import type { AccountsMapMarker } from '@/composables/useAccountsMapboxLegacy'
import {
	ACCOUNT_STATUS_COLORS,
	ACTIVE,
	IGNORE,
	INACTIVE,
	LEAD,
	OPPORTUNITY,
	PENDING_ACCEPTANCE,
	PENDING_INSTALL,
	PROSPECT
} from '@/constants/accounts/statuses'
import type { AccountStatus } from '@/types/account-statuses'
import type {
	CableStatuses,
	MapboxServiceLocationFeature,
	PortalMapAccountListResponse,
	ServiceLocationProperties
} from '@accounts/types'

/**
 * Maps cable status object to flat properties
 */
export const mapCableStatuses = (cableStatuses: CableStatuses) => ({
	cablePulled: cableStatuses.pulled,
	cableSpliced: cableStatuses.spliced,
	cableTerminated: cableStatuses.terminated
})

/**
 * Maps latitude/longitude strings to Google Maps position object
 */
export const mapPosition = (latitude: string, longitude: string): google.maps.LatLngLiteral => ({
	lat: parseFloat(latitude),
	lng: parseFloat(longitude)
})

/**
 * Maps position object to coordinates array [lng, lat] for Mapbox
 */
export const mapCoordinates = (position: google.maps.LatLngLiteral): number[] => [
	position.lng,
	position.lat
]

/**
 * Gets color for account status
 */
export const getAccountColor = (accountStatus: AccountStatus): string => {
	return ACCOUNT_STATUS_COLORS[accountStatus as keyof typeof ACCOUNT_STATUS_COLORS] || ''
}

/**
 * Gets z-index for account status (rendering priority on map)
 * Higher z-index = renders on top
 */
export const getAccountZIndex = (accountStatus: string): number => {
	switch (accountStatus) {
		case LEAD:
			return 1
		case PROSPECT:
			return 2
		case OPPORTUNITY:
			return 3
		case PENDING_INSTALL:
			return 4
		case PENDING_ACCEPTANCE:
			return 5
		case ACTIVE:
			return 6
		case INACTIVE:
			return 7
		case IGNORE:
			return 8
		default:
			return 9
	}
}

/**
 * Converts any ID to string format
 */
export const toStringId = (id: number | string): string => {
	return id?.toString() || ''
}

/**
 * Converts any ID to number format
 */
export const toNumberId = (id: number | string): number => {
	return typeof id === 'number' ? id : parseInt(id, 10) || 0
}

/**
 * Converts boolean to 'true'/'false' string
 */
export const booleanToString = (value: boolean): string => {
	return value ? 'true' : 'false'
}

// =============================================================================
// Feature Mapping Functions
// =============================================================================

/**
 * Maps API response to ServiceLocationProperties
 * This is the primary mapping function from API → App state
 *
 * @param response - Raw API response from /portal/map/accounts/list
 * @returns ServiceLocationProperties with all mapped fields
 */
export const mapToServiceLocationProperties = (
	response: PortalMapAccountListResponse
): ServiceLocationProperties => {
	const {
		accountId,
		accountStatus,
		latitude,
		longitude,
		zoneId,
		siteSurveyStatus,
		insideInstallStatus,
		outsideInstallStatus,
		cableStatuses,
		templineStatus,
		hasEquipment,
		hasTags
	} = response

	return {
		accountId,
		accountStatus: (accountStatus || '') as AccountStatus,
		zoneId,
		siteSurveyStatus,
		insideInstallStatus,
		outsideInstallStatus,
		...mapCableStatuses(cableStatuses),
		templineStatus: templineStatus || '',
		hasEquipment,
		hasTags,
		position: mapPosition(latitude, longitude),
		color: getAccountColor(accountStatus),
		zIndex: getAccountZIndex(accountStatus)
	}
}

/**
 * Maps ServiceLocationProperties to MapboxServiceLocationFeature
 * This converts app state → Mapbox GeoJSON feature format
 *
 * @param properties - ServiceLocationProperties from app state
 * @returns MapboxServiceLocationFeature for Mapbox rendering
 */
export const mapToMapboxFeature = (
	properties: ServiceLocationProperties
): MapboxServiceLocationFeature => {
	return {
		accountId: properties.accountId,
		type: 'Point',
		coordinates: mapCoordinates(properties.position),
		accountStatus: properties.accountStatus,
		zoneId: properties.zoneId,
		siteSurveyStatus: properties.siteSurveyStatus,
		insideInstallStatus: properties.insideInstallStatus,
		outsideInstallStatus: properties.outsideInstallStatus,
		cablePulled: !!properties.cablePulled,
		cableSpliced: !!properties.cableSpliced,
		cableTerminated: !!properties.cableTerminated,
		templineStatus: properties.templineStatus,
		hasEquipment: properties.hasEquipment,
		hasTags: properties.hasTags
	}
}

/**
 * Batch mapper: API response array → ServiceLocationProperties array
 */
export const mapServiceLocationFeatures = (
	responseArray: PortalMapAccountListResponse[]
): ServiceLocationProperties[] => {
	return responseArray.map(mapToServiceLocationProperties)
}

/**
 * Batch mapper: ServiceLocationProperties array → MapboxServiceLocationFeature array
 */
export const mapAccountsMarkers = (
	propertiesArray: ServiceLocationProperties[]
): MapboxServiceLocationFeature[] => {
	return propertiesArray.map(mapToMapboxFeature)
}

/**
 * Converts MapboxServiceLocationFeature to legacy AccountsMapMarker format
 * @deprecated For backward compatibility with legacy code
 */
export const mapToLegacyAccountsMarker = (
	feature: MapboxServiceLocationFeature
): AccountsMapMarker => {
	return {
		id: toStringId(feature.accountId),
		type: 'Point',
		zoneId: toStringId(feature.zoneId),
		accountStatus: feature.accountStatus,
		coordinates: Array.isArray(feature.coordinates) ? feature.coordinates : [ 0, 0 ],
		surveyStatus: feature.siteSurveyStatus,
		insideInstallStatus: feature.insideInstallStatus,
		outsideInstallStatus: feature.outsideInstallStatus,
		cablePulled: feature.cablePulled,
		cableSpliced: feature.cableSpliced,
		cableTerminated: feature.cableTerminated,
		templineStatus: feature.templineStatus,
		hasEquipment: booleanToString(feature.hasEquipment)
	}
}

/**
 * Batch mapper: MapboxServiceLocationFeature array → legacy AccountsMapMarker array
 * @deprecated For backward compatibility with legacy code
 */
export const mapToLegacyAccountsMarkers = (
	features: MapboxServiceLocationFeature[]
): AccountsMapMarker[] => {
	return features.map(mapToLegacyAccountsMarker)
}
